import { MutationTree } from "vuex";
import Vue from "vue";
import {
  ILoyaltyState,
  ICustomerSearchResponse,
  ICustomer,
  ICustomerCard,
  IPointsListResponse,
  ITransactionListResponse,
  IAccountOffer,
  IEarnRate,
  ICampaign,
  LoyaltySchemeType
} from "./types";
import { cloneDeep } from "@/helpers/object";
import { localISOString } from "@/helpers/date";

export namespace LOYALTY_MUTATIONS {
  export const TOGGLE_IS_LOADING = "toggleIsLoading";
  export const SET_SEGMENT_OFFER = "setSegmentOffer";
  export const SET_SEGMENT_OFFER_ORIGINAL = "setSegmentOfferOriginal";
  export const SET_SEGMENT_OFFER_LIST = "setSegmentOfferList";

  export const SET_SEGMENT_OFFER_IS_ELIGIBLE = "setSegmentOfferIsEligible";
  export const SET_SEGMENT_OFFER_SEGMENT = "setSegmentOfferSegment";
  export const SET_SEGMENT_OFFER_LEVEL_TYPE = "setSegmentOfferLevelType";
  export const SET_SEGMENT_OFFER_CHANNELS = "setSegmentOfferChannels";
  export const SET_SEGMENT_OFFER_FLYBUYS_EARN_RATE_QUANTITY =
    "setSegmentOfferFlybuysEarnRateQuantity";
  export const SET_SEGMENT_OFFER_AIRPOINTS_EARN_RATE_QUANTITY =
    "setSegmentOfferAirpointsEarnRateQuantity";
  export const SET_CHANNEL_REFERENCE_DATA = "setChannelReferenceData";
  export const SET_SEGMENT_REFERENCE_DATA = "setSegmentReferenceData";

  export const SET_CUSTOMER_SEARCH_IN_PROGRESS = "setCustomerSearchInProgress";

  export const SET_ACCOUNT_OFFER_LIST = "setSpecialOfferList";
  export const SET_ACCOUNT_OFFER_LIST_PAGINATION =
    "setAccountOfferListPagination";
  export const SET_ACCOUNT_OFFER = "setAccountOffer";
  export const SET_ACCOUNT_OFFER_IS_ELIGIBLE = "setAccountOfferIsEligible";
  export const SET_ACCOUNT_OFFER_LEVEL_TYPE = "setAccountOfferLevelType";
  export const SET_ACCOUNT_OFFER_FLYBUYS_EARN_RATE_QUANTITY =
    "setAccountOfferFlybuysEarnRateQuantity";
  export const SET_ACCOUNT_OFFER_AIRPOINTS_EARN_RATE_QUANTITY =
    "setAccountOfferAirpointsEarnRateQuantity";
  export const SET_ACCOUNT_OFFER_CHANNELS = "setAccountOfferChannels";
  export const SET_ACCOUNT_OFFER_CUSTOMER = "setAccountOfferCustomer";

  export const SET_CUSTOMER_SEARCH_LIST = "setCustomerSearchList";

  export const SET_CUSTOMER_LIST = "setCustomerList";
  export const SET_CUSTOMER_LIST_PAGINATION = "setCustomerListPagination";
  export const SET_CUSTOMER = "setCustomer";
  export const SET_CUSTOMER_CARDS = "setCustomerCards";
  export const SET_CUSTOMER_POINTS_LIST = "setCustomerPoints";
  export const SET_CUSTOMER_TRANSACTION_LIST = "setTransactionList";
  export const SET_CUSTOMER_CARDS_LIST_PAGINATION =
    "setCustomerCardsListPagination";
  export const SET_CUSTOMER_CARDS_SHOW_MODAL = "setCustomerCardsShowModal";
  export const SET_ACTIVE_CUSTOMER_CARD = "setActiveCustomerCard";
  export const SET_CAMPAIGN = "setCampaign";

  export const SET_CAMPAIGN_LIST = "setCampaignList";

  export const SET_CAMPAIGN_AIRPOINTS_MULTIPLIER =
    "setCampaignAirpointsMultiplier";
  export const SET_CAMPAIGN_FLYBUYS_MULTIPLIER = "setCampaignFlybuysMultiplier";
  export const SET_CAMPAIGN_NAME = "setCampaignName";
  export const SET_CAMPAIGN_STARTDATE = "setCampaignStartDate";
  export const SET_CAMPAIGN_ENDDATE = "setCampaignEndDate";
  export const SET_CAMPAIGN_SEGMENTS = "setCampaignSegments";
  export const SET_CAMPAIGN_CHANNELS = "setCampaignChannels";

  export const SET_ACCOUNT_OFFER_PAGINATION = "setAccountOfferPagination";
  export const SET_SEGMENT_OFFER_PAGINATION = "setSegmentOfferPagination";
  export const SET_CAMPAIGNS_PAGINATION = "setCampaignsPagination";
  export const SET_CUSTOMER_CARDS_PAGINATION = "setCustomerCardsPagination";
}

export const mutations: MutationTree<ILoyaltyState> = {
  [LOYALTY_MUTATIONS.TOGGLE_IS_LOADING](
    state: ILoyaltyState,
    isLoading: boolean
  ): void {
    state.isLoading = isLoading;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER](
    state: ILoyaltyState,
    segmentOffer
  ): void {
    // create a more user friendly model of account offer
    const segmentOfferWithEarnRates: IAccountOffer = {
      ...segmentOffer,
      airpointsEarnRateQuantity:
        (
          (segmentOffer.earnRates || []).find(
            (v: any) => v.loyaltyScheme === LoyaltySchemeType.Airpoints
          ) || ({} as IEarnRate)
        ).quantity || 0,
      flyBuysEarnRateQuantity:
        (
          (segmentOffer.earnRates || []).find(
            (v: any) => v.loyaltyScheme === LoyaltySchemeType.FlyBuys
          ) || ({} as IEarnRate)
        ).quantity || 0
    };

    state.segmentOffer = cloneDeep(segmentOfferWithEarnRates);
    state.segmentOfferOriginal = cloneDeep(segmentOfferWithEarnRates);
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_LIST](
    state: ILoyaltyState,
    segmentOfferList
  ): void {
    state.segmentOfferList = segmentOfferList;
  },

  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_IS_ELIGIBLE](
    state: ILoyaltyState,
    isEligible
  ): void {
    state.segmentOffer.isEligible = isEligible;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_SEGMENT](
    state: ILoyaltyState,
    segment
  ): void {
    state.segmentOffer.segment = { ...segment };
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_LEVEL_TYPE](
    state: ILoyaltyState,
    offerLevelType
  ): void {
    state.segmentOffer.level = offerLevelType;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_FLYBUYS_EARN_RATE_QUANTITY](
    state: ILoyaltyState,
    earnRateQuantity
  ): void {
    state.segmentOffer.flyBuysEarnRateQuantity = earnRateQuantity;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_AIRPOINTS_EARN_RATE_QUANTITY](
    state: ILoyaltyState,
    earnRateQuantity
  ): void {
    state.segmentOffer.airpointsEarnRateQuantity = earnRateQuantity;
  },
  [LOYALTY_MUTATIONS.SET_CHANNEL_REFERENCE_DATA](
    state: ILoyaltyState,
    channelList
  ): void {
    state.channelList = channelList;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_REFERENCE_DATA](
    state: ILoyaltyState,
    segmentList
  ): void {
    state.segmentList = segmentList;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_CHANNELS](
    state: ILoyaltyState,
    channels: any[]
  ): void {
    if (state.segmentOffer) {
      state.segmentOffer.channels = channels;
    }
  },

  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_LIST](
    state: ILoyaltyState,
    accountOfferList
  ): void {
    state.accountOfferList = accountOfferList;
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_LIST_PAGINATION](
    state: ILoyaltyState,
    pagination
  ): void {
    state.accountOfferListPaginationTotal = pagination.total;
  },

  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER](
    state: ILoyaltyState,
    accountOffer
  ): void {
    // create a more user friendly model of account offer
    const accountOfferWithEarnRates: IAccountOffer = {
      ...accountOffer,
      airpointsEarnRateQuantity:
        (
          (accountOffer.earnRates || []).find(
            (v: any) => v.loyaltyScheme === LoyaltySchemeType.Airpoints
          ) || ({} as IEarnRate)
        ).quantity || 0,
      flyBuysEarnRateQuantity:
        (
          (accountOffer.earnRates || []).find(
            (v: any) => v.loyaltyScheme === LoyaltySchemeType.FlyBuys
          ) || ({} as IEarnRate)
        ).quantity || 0
    };

    state.accountOffer = cloneDeep(accountOfferWithEarnRates);
    state.accountOfferOriginal = cloneDeep(accountOfferWithEarnRates);
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_IS_ELIGIBLE](
    state: ILoyaltyState,
    isEligible
  ): void {
    state.accountOffer.isEligible = isEligible;
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_LEVEL_TYPE](
    state: ILoyaltyState,
    offerLevelType
  ): void {
    state.accountOffer.level = offerLevelType;
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_FLYBUYS_EARN_RATE_QUANTITY](
    state: ILoyaltyState,
    earnRateQuantity
  ): void {
    Vue.set(state.accountOffer, "flyBuysEarnRateQuantity", earnRateQuantity);
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_AIRPOINTS_EARN_RATE_QUANTITY](
    state: ILoyaltyState,
    earnRateQuantity
  ): void {
    Vue.set(state.accountOffer, "airpointsEarnRateQuantity", earnRateQuantity);
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_CHANNELS](
    state: ILoyaltyState,
    channels: any[]
  ): void {
    if (state.accountOffer) {
      state.accountOffer.channels = channels;
    }
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_CUSTOMER](
    state: ILoyaltyState,
    customer: ICustomerSearchResponse
  ): void {
    Vue.set(
      state,
      "accountOffer",
      Object.assign({}, state.accountOffer, cloneDeep(customer))
    );
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_SEARCH_LIST](
    state: ILoyaltyState,
    customerSearchList
  ): void {
    Vue.set(state, "customerSearchList", customerSearchList);
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_POINTS_LIST](
    state: ILoyaltyState,
    points: IPointsListResponse[]
  ): void {
    state.customerPointsList = points;
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_TRANSACTION_LIST](
    state: ILoyaltyState,
    transactions: ITransactionListResponse[]
  ): void {
    state.customerTransactionList = transactions;
  },

  [LOYALTY_MUTATIONS.SET_CUSTOMER_SEARCH_IN_PROGRESS](
    state: ILoyaltyState,
    customerSearchInProgress
  ): void {
    state.customerSearchInProgress = customerSearchInProgress;
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_LIST](
    state: ILoyaltyState,
    customerList
  ): void {
    state.customerList = customerList;
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_LIST_PAGINATION](
    state: ILoyaltyState,
    pagination
  ): void {
    state.customerListPaginationTotal = pagination.total;
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER](
    state: ILoyaltyState,
    customer: ICustomer
  ): void {
    Vue.set(state, "customer", customer);
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_CARDS](
    state: ILoyaltyState,
    cards: ICustomerCard
  ): void {
    Vue.set(state, "customerCardsList", cards);
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_CARDS_LIST_PAGINATION](
    state: ILoyaltyState,
    pagination
  ): void {
    state.customerCardsListPaginationTotal = pagination.total;
  },
  [LOYALTY_MUTATIONS.SET_ACTIVE_CUSTOMER_CARD](
    state: ILoyaltyState,
    customerCard: ICustomerCard
  ): void {
    Vue.set(state, "activeCustomerCard", customerCard);
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN](
    state: ILoyaltyState,
    campaign: ICampaign
  ): void {
    state.activeCampaign = cloneDeep(campaign);
    state.activeCampaignOriginal = cloneDeep(campaign);
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_LIST](
    state: ILoyaltyState,
    campaignList: ICampaign[]
  ): void {
    state.campaignList = campaignList;
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_AIRPOINTS_MULTIPLIER](
    state: ILoyaltyState,
    multiplier
  ): void {
    // Update the multipliers array
    const multiplierIndex = (state.activeCampaign.multipliers || []).findIndex(
      m => m.loyaltyScheme === LoyaltySchemeType.Airpoints
    );

    const multiplierObject = {
      loyaltyScheme: LoyaltySchemeType.Airpoints,
      multiplier
    };

    if (multiplierIndex >= 0) {
      state.activeCampaign.multipliers.splice(
        multiplierIndex,
        1,
        multiplierObject
      );
    } else {
      state.activeCampaign.multipliers.push(multiplierObject);
    }
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_FLYBUYS_MULTIPLIER](
    state: ILoyaltyState,
    multiplier
  ): void {
    const multiplierIndex = (state.activeCampaign.multipliers || []).findIndex(
      m => m.loyaltyScheme === LoyaltySchemeType.FlyBuys
    );

    const multiplierObject = {
      loyaltyScheme: LoyaltySchemeType.FlyBuys,
      multiplier
    };

    if (multiplierIndex >= 0) {
      state.activeCampaign.multipliers.splice(
        multiplierIndex,
        1,
        multiplierObject
      );
    } else {
      state.activeCampaign.multipliers.push(multiplierObject);
    }
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_NAME](state: ILoyaltyState, name): void {
    if (state.activeCampaign) {
      state.activeCampaign.name = name;
    }
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_STARTDATE](
    state: ILoyaltyState,
    startDate: Date
  ): void {
    if (state.activeCampaign) {
      state.activeCampaign.startDate = localISOString(startDate);
    }
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_ENDDATE](
    state: ILoyaltyState,
    endDate: Date): void {
    if (state.activeCampaign) {
      state.activeCampaign.endDate = localISOString(endDate);
    }
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_SEGMENTS](
    state: ILoyaltyState,
    segments: any[]
  ): void {
    if (state.activeCampaign) {
      state.activeCampaign.segments = segments;
    }
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGN_CHANNELS](
    state: ILoyaltyState,
    channels: any[]
  ): void {
    if (state.activeCampaign) {
      state.activeCampaign.channels = channels;
    }
  },
  [LOYALTY_MUTATIONS.SET_ACCOUNT_OFFER_PAGINATION](state, value) {
    state.dataTables.accountOffers.pagination = value;
  },
  [LOYALTY_MUTATIONS.SET_SEGMENT_OFFER_PAGINATION](state, value) {
    state.dataTables.segmentOffers.pagination = value;
  },
  [LOYALTY_MUTATIONS.SET_CAMPAIGNS_PAGINATION](state, value) {
    state.dataTables.campaigns.pagination = value;
  },
  [LOYALTY_MUTATIONS.SET_CUSTOMER_CARDS_PAGINATION](state, value) {
    state.dataTables.customerCards.pagination = value;
  }
};
